import React, { useEffect, useRef, useState } from 'react';
import { Clock3, Info, ScanLine, Trash2, Warehouse } from 'lucide-react';
import PdaPage from '../components/PdaPage';
import {
  getInventoryTraceByBid,
  InventoryTraceEvent,
  InventoryTraceResponse,
} from '../services/api';
import { usePersistentFocus } from '../hooks/usePersistentFocus';
import { navigateTo } from '../utils/navigation';
import { detectScanCodeKind, normalizeScannedCode } from '../utils/pdaCodes';
import { getPdaErrorMessage } from '../utils/pdaErrors';
import { notifyError, notifySuccess } from '../utils/pdaFeedback';
import { formatDateTime, formatWeight } from '../utils/formatters';

const AUTO_SCAN_DELAY_MS = 120;

function resolveStatusBadge(status: string) {
  if (status === '可用' || status === '在库') {
    return 'raw-trace-status raw-trace-status--green';
  }
  if (status === '锁定' || status === '待检') {
    return 'raw-trace-status raw-trace-status--green';
  }
  if (status === '已出库' || status === '报废') {
    return 'raw-trace-status raw-trace-status--orange';
  }
  return 'raw-trace-status raw-trace-status--gray';
}

function resolveOpTypeBadge(opType: string) {
  if (opType === 'INBOUND' || opType === 'QC_PASS') {
    return 'raw-trace-event__badge raw-trace-event__badge--green';
  }
  if (opType === 'TRANSFER') {
    return 'raw-trace-event__badge raw-trace-event__badge--blue';
  }
  if (opType === 'OUTBOUND' || opType === 'QC_REJECT') {
    return 'raw-trace-event__badge raw-trace-event__badge--orange';
  }
  return 'raw-trace-event__badge raw-trace-event__badge--gray';
}

function resolveOperatorName(event: InventoryTraceEvent) {
  return event.operator?.name || (event.operator?.id ? `操作人 #${event.operator.id}` : '系统');
}

function TraceField({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="raw-trace-field">
      <div className="raw-trace-field__label">{label}</div>
      <div className="raw-trace-field__value">{value || '--'}</div>
    </div>
  );
}

function RawMaterialTrace() {
  const inputRef = useRef<HTMLInputElement>(null);
  const scanTimerRef = useRef<number>(0);
  const [scanValue, setScanValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [record, setRecord] = useState<InventoryTraceResponse | null>(null);

  usePersistentFocus(inputRef, !loading, loading ? 'loading' : 'ready');
  useEffect(() => () => window.clearTimeout(scanTimerRef.current), []);

  function clearScanInput() {
    window.clearTimeout(scanTimerRef.current);
    setScanValue('');
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  }

  function resetTrace() {
    clearScanInput();
    setRecord(null);
  }

  function scheduleLookup(rawCode: string) {
    if (loading) {
      return;
    }
    setScanValue(rawCode);
    window.clearTimeout(scanTimerRef.current);
    scanTimerRef.current = window.setTimeout(() => {
      void handleLookup(inputRef.current?.value ?? rawCode);
    }, AUTO_SCAN_DELAY_MS);
  }

  async function handleLookup(rawCode?: string) {
    const code = normalizeScannedCode(rawCode ?? inputRef.current?.value ?? scanValue);
    const kind = detectScanCodeKind(code);

    if (!code || loading) {
      return;
    }

    if (kind !== 'rawMaterial') {
      if (kind === 'offcut') {
        notifyError('追溯料头请用「追溯」入口');
      } else if (kind === 'location') {
        notifyError('追溯页只接受原材料 BID');
      } else if (kind === 'preInboundOrder') {
        notifyError('这是预入库单 QR,不是原材料 BID');
      } else if (kind === 'equipment') {
        notifyError('设备扫码功能尚未开放');
      } else {
        notifyError('请扫描原材料 BID(RM 开头)');
      }
      clearScanInput();
      return;
    }

    setLoading(true);
    try {
      const response = await getInventoryTraceByBid(code);
      setRecord(response.data);
      notifySuccess(`已查询 ${response.data.base?.bid || code}`);
    } catch (error) {
      notifyError(getPdaErrorMessage(error, '追溯查询失败'));
      setRecord(null);
    } finally {
      setLoading(false);
      clearScanInput();
    }
  }

  const base = record?.base;
  const location = record?.current_location;
  const timeline = record?.timeline || [];

  return (
    <PdaPage
      actionLabel="清空"
      actionIcon={<Trash2 aria-hidden="true" size={14} strokeWidth={2.2} />}
      onAction={resetTrace}
      onBack={() => navigateTo('/home')}
      pageClassName="raw-trace-page"
      subtitle="扫原材料 BID,查看本体信息 + 当前库位 + 时间线"
      title="原材料追溯"
    >
      <section className="raw-trace-card raw-trace-scan-card">
        <div className="raw-trace-card__title">扫码原材料 BID</div>
        <div className="raw-trace-scan-row">
          <div className="raw-trace-input-wrap">
            <ScanLine aria-hidden="true" className="raw-trace-input-icon" size={20} strokeWidth={2.4} />
            <input
              autoComplete="off"
              autoFocus
              autoCapitalize="off"
              autoCorrect="off"
              className="raw-trace-input"
              id="trace-raw-material-bid"
              inputMode="none"
              onInput={(event) => scheduleLookup(event.currentTarget.value)}
              onKeyUp={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  void handleLookup(event.currentTarget.value);
                }
              }}
              placeholder="扫描后按回车查询"
              ref={inputRef}
              spellCheck={false}
            />
          </div>
          <button
            className="raw-trace-query-btn"
            disabled={loading || !scanValue.trim()}
            onClick={() => void handleLookup()}
            type="button"
          >
            {loading ? '查询中' : '查询'}
          </button>
        </div>
      </section>

      {base ? (
        <>
          <section className="raw-trace-card raw-trace-base-card">
            <div className="raw-trace-base-head">
              <span className={resolveStatusBadge(base.status)}>{base.status || '未知'}</span>
              <span className="raw-trace-base-time">
                入库时间：{formatDateTime(base.inbound_at)}
              </span>
            </div>
            <div className="raw-trace-grid raw-trace-grid--two">
              <TraceField label="BID" value={base.bid} />
              <TraceField label="SKU" value={base.sku || '--'} />
            </div>
            <div className="raw-trace-grid raw-trace-grid--three">
              <TraceField
                label="材料"
                value={`${base.material_category || '--'}${base.material_grade ? ` / ${base.material_grade}` : ''}`}
              />
              <TraceField label="批次号" value={base.batch_no || '--'} />
              <TraceField label="检验单号" value={base.inspection_no || '--'} />
            </div>
            <div className="raw-trace-grid raw-trace-grid--three">
              <TraceField label="入库重量" value={`${formatWeight(base.inbound_weight)}kg`} />
              <TraceField label="可用重量" value={`${formatWeight(base.avail_weight)}kg`} />
              <TraceField
                label="长度"
                value={base.inbound_length ? `${formatWeight(base.inbound_length)}mm` : '--'}
              />
            </div>
            <div className="raw-trace-grid raw-trace-grid--three raw-trace-grid--last">
              <TraceField label="熔炼炉号" value={base.smelt_heat_no || '--'} />
              <TraceField label="工业牌号" value={base.industry_grade || '--'} />
              <TraceField label="供应商" value={base.supplier_name || '--'} />
            </div>
            {base.parent_bid || (base.child_bids && base.child_bids.length > 0) ? (
              <div className="raw-trace-extra">
                {base.parent_bid ? <div>切割来源:{base.parent_bid}</div> : null}
                {base.child_bids && base.child_bids.length > 0 ? (
                  <div>切割产出:{base.child_bids.join(' / ')}</div>
                ) : null}
              </div>
            ) : null}
            {base.note ? (
              <div className="raw-trace-extra">备注:{base.note}</div>
            ) : null}
          </section>

          <section className="raw-trace-card raw-trace-location-card">
            <div className="raw-trace-section-head">
              <div className="raw-trace-section-title">
                <span className="raw-trace-section-icon">
                  <Warehouse aria-hidden="true" size={17} strokeWidth={2.4} />
                </span>
                当前库位
              </div>
              <div className="raw-trace-section-desc">{location?.description || '未上架或已出库'}</div>
            </div>
            {location ? (
              <div className="raw-trace-grid raw-trace-grid--four raw-trace-grid--last">
                <TraceField label="库位" value={location.loc_code || '--'} />
                <TraceField label="仓库" value={location.warehouse || '--'} />
                <TraceField label="库区" value={location.area || '--'} />
                <TraceField label="类型" value={location.warehouse_type || '--'} />
              </div>
            ) : (
              <div className="raw-trace-empty-line">未上架或已出库。</div>
            )}
          </section>

          <section className="raw-trace-card raw-trace-timeline-card">
            <div className="raw-trace-section-head raw-trace-section-head--plain">
              <div className="raw-trace-section-title">
                <span className="raw-trace-section-icon">
                  <Clock3 aria-hidden="true" size={17} strokeWidth={2.4} />
                </span>
                时间线（{timeline.length} 条）
              </div>
            </div>
            <div className="raw-trace-timeline-list">
              {timeline.length === 0 ? (
                <div className="raw-trace-empty-line">还没有移动记录。</div>
              ) : (
                timeline.slice(0, 20).map((event, index) => (
                  <div className="raw-trace-event" key={`${event.at}-${event.op_type}-${index}`}>
                    <div className="raw-trace-event__rail">
                      <span className="raw-trace-event__dot" />
                    </div>
                    <div className="raw-trace-event__body">
                      <div className="raw-trace-event__top">
                        <div className="raw-trace-event__main">
                          <span className={resolveOpTypeBadge(event.op_type)}>
                            {event.op_type_name || event.op_type}
                          </span>
                          {(event.from_loc || event.to_loc) ? (
                            <span className="raw-trace-event__path">
                              {event.from_loc || '无库位'}
                              {' → '}
                              {event.to_loc || '无库位'}
                            </span>
                          ) : null}
                        </div>
                      </div>
                      <div className="raw-trace-event__detail">
                        <span>重量：{formatWeight(event.weight)}kg</span>
                        <span>操作人：{resolveOperatorName(event)}</span>
                        <span>时间：{formatDateTime(event.at)}</span>
                      </div>
                      {event.note ? (
                        <div className="raw-trace-event__note">备注:{event.note}</div>
                      ) : null}
                    </div>
                  </div>
                ))
              )}
            </div>
          </section>
        </>
      ) : null}

      <section className="raw-trace-tip">
        <Info aria-hidden="true" size={16} strokeWidth={2.4} />
        <span>
          扫描原材料 BID 后，这里会显示本体信息（SKU / 批次 / 炉号 / 供应商 / 切割父子关系）、当前库位、最近 20 条时间线。
        </span>
      </section>
    </PdaPage>
  );
}

export default RawMaterialTrace;
